import React from "react";
import { Toggle } from "../../../components/Toggle/Toggle";
import { useTheme } from "../../../contexts/Theme/ThemeContext";
import {
  AREAS_PERMISSOES,
  AreaPermissao,
  AcaoPermissao,
  OpcaoEscopoUnico,
} from "../../../services/Consts";
import { ViewPainelPermissoes } from "./styles";

interface PainelPermissoesProps {
  permissoes: number[];
  onChange: (permissoes: number[]) => void;
  // Flags externas para pílulas condicionais (ex.: Relatórios "Personalizados").
  contexto?: { [flag: string]: boolean };
}

// ids concedidos por uma ação (par de escopo ou id único).
const idsDaAcao = (a: AcaoPermissao): number[] =>
  a.escopo ? [a.escopo.geral, a.escopo.meus] : a.id != null ? [a.id] : [];

// id padrão ao ativar uma ação (escopo => "meus", que é o menos permissivo).
const idPadraoAcao = (a: AcaoPermissao): number =>
  a.escopo ? a.escopo.meus : (a.id as number);

// Todos os ids que uma área controla (para limpar a área ao desligar).
const idsDaArea = (area: AreaPermissao): number[] => {
  if (area.tipo === "toggle") return area.id != null ? [area.id] : [];
  if (area.tipo === "escopo-unico")
    return (area.opcoes || []).flatMap((o) => o.ids);
  return (area.acoes || []).flatMap(idsDaAcao);
};

export const PainelPermissoes: React.FC<PainelPermissoesProps> = ({
  permissoes,
  onChange,
  contexto,
}) => {
  const { theme } = useTheme();
  const ids = Array.isArray(permissoes) ? permissoes : [];
  const has = (id: number) => ids.includes(id);

  // Aplica uma edição sobre um Set e emite o novo conjunto (sem duplicados).
  const editar = (fn: (s: Set<number>) => void) => {
    const s = new Set<number>(ids);
    fn(s);
    onChange(Array.from(s));
  };

  // ---- leitura de estado -------------------------------------------------
  const acaoAtiva = (a: AcaoPermissao) => idsDaAcao(a).some(has);
  const escopoAtual = (a: AcaoPermissao): "geral" | "meus" | null =>
    a.escopo ? (has(a.escopo.geral) ? "geral" : has(a.escopo.meus) ? "meus" : null) : null;
  const opcaoAtual = (area: AreaPermissao): OpcaoEscopoUnico | undefined =>
    (area.opcoes || []).find((o) => o.ids.every(has));
  const areaAtiva = (area: AreaPermissao): boolean => {
    if (area.tipo === "toggle") return area.id != null && has(area.id);
    if (area.tipo === "escopo-unico") return !!opcaoAtual(area);
    return (area.acoes || []).some(acaoAtiva);
  };
  // "Visualizar" não pode ser desligada enquanto uma ação dependente estiver ligada.
  const acaoTravada = (area: AreaPermissao, a: AcaoPermissao) =>
    (area.acoes || []).some(
      (x) => x.key !== a.key && acaoAtiva(x) && (x.ativa || []).includes(a.key),
    );
  const acaoVisivel = (a: AcaoPermissao) =>
    !a.condicional || !!(contexto && contexto[a.condicional]);

  // ---- edição ------------------------------------------------------------
  const ativarAcao = (s: Set<number>, area: AreaPermissao, a: AcaoPermissao) => {
    if (a.exclusiva) {
      idsDaArea(area).forEach((id) => s.delete(id));
    } else {
      // marcar uma ação específica desliga a exclusiva ("Todos") da área.
      (area.acoes || [])
        .filter((x) => x.exclusiva)
        .forEach((x) => idsDaAcao(x).forEach((id) => s.delete(id)));
    }
    if (!idsDaAcao(a).some((id) => s.has(id))) s.add(idPadraoAcao(a));
    (a.ativa || []).forEach((depKey) => {
      const dep = (area.acoes || []).find((x) => x.key === depKey);
      if (dep && !idsDaAcao(dep).some((id) => s.has(id))) s.add(idPadraoAcao(dep));
    });
  };

  const toggleAcao = (area: AreaPermissao, a: AcaoPermissao, on: boolean) =>
    editar((s) => {
      if (on) {
        ativarAcao(s, area, a);
      } else {
        if (acaoTravada(area, a)) return;
        idsDaAcao(a).forEach((id) => s.delete(id));
      }
    });

  const setEscopo = (a: AcaoPermissao, escopo: "geral" | "meus") =>
    editar((s) => {
      if (!a.escopo) return;
      s.delete(a.escopo.geral);
      s.delete(a.escopo.meus);
      s.add(escopo === "geral" ? a.escopo.geral : a.escopo.meus);
    });

  const toggleArea = (area: AreaPermissao, on: boolean) =>
    editar((s) => {
      idsDaArea(area).forEach((id) => s.delete(id));
      if (!on) return;
      if (area.tipo === "toggle") {
        if (area.id != null) s.add(area.id);
      } else if (area.tipo === "escopo-unico") {
        (area.opcoes?.[0]?.ids || []).forEach((id) => s.add(id));
      } else {
        const keys =
          area.padraoKeys && area.padraoKeys.length
            ? area.padraoKeys
            : area.acoes && area.acoes.length
            ? [area.acoes[0].key]
            : [];
        keys.forEach((k) => {
          const a = (area.acoes || []).find((x) => x.key === k);
          if (a) ativarAcao(s, area, a);
        });
      }
    });

  const setOpcao = (area: AreaPermissao, opcao: OpcaoEscopoUnico) =>
    editar((s) => {
      (area.opcoes || []).forEach((o) => o.ids.forEach((id) => s.delete(id)));
      opcao.ids.forEach((id) => s.add(id));
    });

  // ---- render ------------------------------------------------------------
  const renderAcoes = (area: AreaPermissao) => (
    <div className="pills">
      {(area.acoes || []).filter(acaoVisivel).map((a) => {
        const ativa = acaoAtiva(a);
        const travada = ativa && acaoTravada(area, a);
        return (
          <div className="acao-wrap" key={a.key}>
            <button
              type="button"
              className={`pill ${ativa ? "active" : ""} ${travada ? "locked" : ""}`}
              title={travada ? "Necessária para as demais ações ativas" : undefined}
              onClick={() => toggleAcao(area, a, !ativa)}
            >
              {a.label}
            </button>
            {ativa && a.escopo && (
              <div className="subpills">
                <button
                  type="button"
                  className={`pill sub ${escopoAtual(a) === "geral" ? "active" : ""}`}
                  onClick={() => setEscopo(a, "geral")}
                >
                  {a.escopo.labelGeral || "Geral"}
                </button>
                <button
                  type="button"
                  className={`pill sub ${escopoAtual(a) === "meus" ? "active" : ""}`}
                  onClick={() => setEscopo(a, "meus")}
                >
                  {a.escopo.labelMeus || "Meus"}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderEscopoUnico = (area: AreaPermissao) => {
    const atual = opcaoAtual(area);
    return (
      <div className="pills">
        {(area.opcoes || []).map((o) => (
          <button
            type="button"
            key={o.key}
            className={`pill ${atual?.key === o.key ? "active" : ""}`}
            onClick={() => setOpcao(area, o)}
          >
            {o.label}
          </button>
        ))}
      </div>
    );
  };

  return (
    <ViewPainelPermissoes theme={theme} className="fonteTexto">
      {AREAS_PERMISSOES.map((area) => {
        const ativa = areaAtiva(area);
        return (
          <div className="area" key={area.key}>
            <div className="area-header">
              <Toggle value={ativa} onChange={(v) => toggleArea(area, v)} />
              <span className="area-label" onClick={() => toggleArea(area, !ativa)}>
                {area.label}
              </span>
              {area.descricao && (
                <span className="area-info">
                  <span className="area-info-icon">i</span>
                  <span className="tooltip">{area.descricao}</span>
                </span>
              )}
            </div>
            {ativa && area.tipo !== "toggle" && (
              <div className="area-indent">
                {area.tipo === "acoes" && renderAcoes(area)}
                {area.tipo === "escopo-unico" && renderEscopoUnico(area)}
              </div>
            )}
          </div>
        );
      })}
    </ViewPainelPermissoes>
  );
};
